import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { partnersApi } from '../../api/partners';
import { partnerReportsApi } from '../../api/partner-dashboard-reports';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';

type ReportKey = 'partner_register' | 'dd_status' | 'risk_register' | 'high_risk' | 'conditional' | 'expiring_documents' | 'mandatory_failures' | 'partner_performance';

const REPORT_LABELS: Record<ReportKey, string> = {
  partner_register: 'Partner Register', dd_status: 'Due Diligence Status', risk_register: 'Risk Register', high_risk: 'High-Risk Partners',
  conditional: 'Conditional Approvals', expiring_documents: 'Expiring Documents', mandatory_failures: 'Mandatory Criteria Failures', partner_performance: 'Partner Performance',
};

/**
 * Partner Due Diligence Reports (spec section 50). Several report
 * types here are genuinely just filtered views of the existing
 * partner list - not duplicated data models, exactly the same
 * underlying data the Partner Registry itself uses.
 */
export function PartnerDueDiligenceReportsPage() {
  const [activeReport, setActiveReport] = useState<ReportKey>('partner_register');

  const allPartnersQuery = useQuery({ queryKey: ['report-all-partners'], queryFn: () => partnersApi.list(), enabled: ['partner_register', 'dd_status', 'risk_register'].includes(activeReport) });
  const highRiskQuery = useQuery({ queryKey: ['report-high-risk'], queryFn: () => partnersApi.list({ riskRating: 'high' }), enabled: activeReport === 'high_risk' });
  const conditionalQuery = useQuery({ queryKey: ['report-conditional'], queryFn: () => partnersApi.list({ ddStatus: 'approved_with_conditions' }), enabled: activeReport === 'conditional' });
  const expiringDocsQuery = useQuery({ queryKey: ['report-expiring-docs'], queryFn: () => partnerReportsApi.getExpiringDocuments(90), enabled: activeReport === 'expiring_documents' });
  const mandatoryFailuresQuery = useQuery({ queryKey: ['report-mandatory-failures'], queryFn: () => partnerReportsApi.getMandatoryCriteriaFailures(), enabled: activeReport === 'mandatory_failures' });
  const performanceQuery = useQuery({ queryKey: ['report-performance'], queryFn: () => partnerReportsApi.getPartnerPerformance(), enabled: activeReport === 'partner_performance' });

  return (
    <div className="page">
      <PageHeader title="Partner Due Diligence Reports" />

      <div className="panel">
        <div className="panel-body">
          <div className="inline-form">
            {Object.entries(REPORT_LABELS).map(([key, label]) => (
              <button key={key} onClick={() => setActiveReport(key as ReportKey)} style={activeReport === key ? { fontWeight: 700 } : {}}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="panel">
        <SectionHeader title={REPORT_LABELS[activeReport]} />
        <div className="panel-body">
          {(activeReport === 'partner_register' || activeReport === 'dd_status' || activeReport === 'risk_register') && allPartnersQuery.data && (
            <DataTable
              rows={allPartnersQuery.data.data}
              rowKey={(p) => p.id}
              emptyMessage="No partners."
              columns={[
                { header: 'Reference', render: (p) => p.partner_reference }, { header: 'Legal Name', render: (p) => p.legal_name },
                { header: 'DD Status', render: (p) => p.due_diligence_status }, { header: 'Risk', render: (p) => p.current_risk_rating ?? '-' },
                { header: 'Partnership Status', render: (p) => p.partnership_status },
              ]}
            />
          )}
          {activeReport === 'high_risk' && highRiskQuery.data && (
            <DataTable rows={highRiskQuery.data.data} rowKey={(p) => p.id} emptyMessage="No high-risk partners." columns={[{ header: 'Reference', render: (p) => p.partner_reference }, { header: 'Legal Name', render: (p) => p.legal_name }, { header: 'Last Review', render: (p) => p.last_review_date ?? '-' }]} />
          )}
          {activeReport === 'conditional' && conditionalQuery.data && (
            <DataTable rows={conditionalQuery.data.data} rowKey={(p) => p.id} emptyMessage="No conditional approvals." columns={[{ header: 'Reference', render: (p) => p.partner_reference }, { header: 'Legal Name', render: (p) => p.legal_name }]} />
          )}
          {activeReport === 'expiring_documents' && expiringDocsQuery.data && (
            <DataTable rows={expiringDocsQuery.data.data} rowKey={(d) => d.id} emptyMessage="No documents expiring in the next 90 days." columns={[{ header: 'Partner', render: (d) => d.legal_name }, { header: 'Document', render: (d) => d.title }, { header: 'Expiry', render: (d) => d.expiry_date }]} />
          )}
          {activeReport === 'mandatory_failures' && mandatoryFailuresQuery.data && (
            <DataTable rows={mandatoryFailuresQuery.data.data} rowKey={(f) => f.criterion_name} emptyMessage="No mandatory criteria failures on record." columns={[{ header: 'Criterion', render: (f) => f.criterion_name }, { header: 'Low-Score Count', render: (f) => f.low_score_count }]} />
          )}
          {activeReport === 'partner_performance' && performanceQuery.data && (
            <DataTable
              rows={performanceQuery.data.data}
              rowKey={(p) => p.legal_name}
              emptyMessage="No project engagements on record."
              columns={[
                { header: 'Partner', render: (p) => p.legal_name }, { header: 'Total Engagements', render: (p) => p.total_engagements },
                { header: 'Strong Ratings', render: (p) => p.strong_ratings }, { header: 'Weak Ratings', render: (p) => p.weak_ratings },
                { header: 'Engagements with Audit Findings', render: (p) => p.engagements_with_audit_findings },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
